import React, { useState, useRef, useCallback } from 'react';
import { MarkedRange } from '../../types/training';
import { getMarkedSegments, removeTextMark, clearAllMarks } from '../../utils/markingUtils';
import './TextMarkingEditor.css';

interface TextMarkingEditorProps {
  text: string;
  marks?: MarkedRange[];
  onMarksChange: (marks: MarkedRange[]) => void;
  selectedColor: string;
  isEditMode: boolean;
  onAddMark?: (start: number, end: number, color: string) => void;
  onRemoveMark?: (markId: string) => void;
}

const TextMarkingEditor: React.FC<TextMarkingEditorProps> = ({
  text,
  marks = [],
  onMarksChange,
  selectedColor,
  isEditMode,
  onAddMark,
  onRemoveMark,
}) => {
  const [selection, setSelection] = useState<{ start: number; end: number } | null>(null);
  const textContainerRef = useRef<HTMLDivElement>(null);

  // Get segments with marks applied
  const segments = getMarkedSegments(text, marks);

  // Handle text selection
  const handleTextSelection = useCallback(() => {
    if (!isEditMode) return;

    const selectedText = window.getSelection();
    if (!selectedText || selectedText.toString().length === 0) {
      setSelection(null);
      return;
    }

    try {
      const range = selectedText.getRangeAt(0);
      const preCaretRange = range.cloneRange();
      preCaretRange.selectNodeContents(textContainerRef.current!);
      preCaretRange.setEnd(range.endContainer, range.endOffset);
      const start = preCaretRange.toString().length;
      const end = start + selectedText.toString().length;

      setSelection({ start, end });
    } catch (e) {
      console.error('Error getting selection:', e);
    }
  }, [isEditMode]);

  // Handle mouse up after selection
  const handleMouseUp = useCallback(() => {
    handleTextSelection();
  }, [handleTextSelection]);

  // Handle adding a mark
  const handleAddMark = useCallback(() => {
    if (!selection || !isEditMode) return;

    if (onAddMark) {
      onAddMark(selection.start, selection.end, selectedColor);
    }

    setSelection(null);
    window.getSelection()?.removeAllRanges();
  }, [selection, isEditMode, onAddMark, selectedColor]);

  // Handle removing a mark
  const handleRemoveMarkClick = useCallback(
    (markId: string) => {
      if (onRemoveMark) {
        onRemoveMark(markId);
      }
    },
    [onRemoveMark]
  );

  // Handle clearing all marks
  const handleClearAll = useCallback(() => {
    onMarksChange(clearAllMarks());
  }, [onMarksChange]);

  return (
    <div className="text-marking-editor">
      {/* Text Display Area */}
      <div className="text-container-wrapper">
        <div
          ref={textContainerRef}
          className={`text-container ${isEditMode ? 'edit-mode' : 'view-mode'}`}
          onMouseUp={handleMouseUp}
          onTouchEnd={handleMouseUp}
        >
          {/* Render text with marks */}
          {segments.map((segment, idx) => (
            <span
              key={idx}
              className={segment.isMarked ? 'marked-segment' : 'unmarked-segment'}
              style={
                segment.isMarked
                  ? {
                      backgroundColor: segment.color,
                      opacity: 0.6,
                    }
                  : {}
              }
              title={
                segment.isMarked
                  ? `Mark: ${segment.text.substring(0, 50)}...`
                  : undefined
              }
            >
              {segment.text}
            </span>
          ))}
        </div>

        {/* Selection Indicator and Add Mark Button */}
        {isEditMode && selection && selection.end > selection.start && (
          <div className="selection-actions">
            <button className="add-mark-btn" onClick={handleAddMark}>
              Mark Selected
            </button>
            <span className="selection-info">
              {selection.end - selection.start} characters selected
            </span>
          </div>
        )}
      </div>

      {/* Marks List */}
      {marks && marks.length > 0 && (
        <div className="marks-list-section">
          <div className="marks-list-header">
            <h4>Marks ({marks.length})</h4>
          </div>
          <div className="marks-list">
            {marks.map((mark) => {
              const markedText = text.substring(
                mark.start,
                Math.min(mark.end, mark.start + 60)
              );
              const isTruncated = mark.end - mark.start > 60;

              return (
                <div key={mark.id} className="mark-item">
                  <div
                    className="mark-color-indicator"
                    style={{ backgroundColor: mark.color }}
                  />
                  <div className="mark-content">
                    <div className="mark-text">
                      "{markedText}
                      {isTruncated ? '...' : ''}
                      "
                    </div>
                    <div className="mark-position">
                      Position: {mark.start}-{mark.end}
                    </div>
                  </div>
                  {isEditMode && (
                    <button
                      className="remove-mark-btn"
                      onClick={() => handleRemoveMarkClick(mark.id)}
                      title="Remove mark"
                    >
                      ✕
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Empty State */}
      {(!marks || marks.length === 0) && (
        <div className="empty-state">
          {isEditMode ? (
            <>
              <p>👆 Select text to mark it</p>
              <p className="secondary">Choose a color from the toolbar</p>
            </>
          ) : (
            <p>No marks in this file</p>
          )}
        </div>
      )}
    </div>
  );
};

export default TextMarkingEditor;
